if (window.cartChecker) {
  // Already loaded (theme quick-add-modal + HEOA/CMRP page scripts both include this file).
} else {
class CartChecker {
  constructor() {
    /** @type {Array<any>} */
    this.cartItems = [];
    this.isLoading = false;
    /** @type {Promise<Array<any>> | null} */
    this.inflight = null;
    this.inflightEpoch = 0;
    this.cartEpoch = 0;
    this.lastFetchTime = 0;
    this.cacheTimeout = 30000;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.refreshTimer = null;
    this.setupCartEventListeners();
  }

  setupCartEventListeners() {
    const onCartEvent = (event) => this.handleCartEvent(event);
    document.addEventListener('cart:add', onCartEvent);
    document.addEventListener('cart:update', onCartEvent);
    document.addEventListener('cart:change', onCartEvent);
  }

  /**
   * Drop the 30s cache immediately so callers (PDP badges) never read the
   * previous add. If the event already includes cart.items, use that.
   * @param {Event} event
   */
  handleCartEvent(event) {
    this.invalidateLocalCache();
    if (this.applyCartItemsFromEvent(event)) return;
    this.scheduleRefresh();
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  applyCartItemsFromEvent(event) {
    const items = event?.detail?.resource?.items;
    if (!Array.isArray(items)) return false;
    this.cartItems = items;
    this.lastFetchTime = Date.now();
    this.isLoading = false;
    this.inflight = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    return true;
  }

  invalidateLocalCache() {
    this.cartEpoch += 1;
    this.lastFetchTime = 0;
  }

  scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refreshCartData();
    }, 250);
  }

  /**
   * Normalize Shopify variant IDs so GID and numeric forms match cart.js.
   * @param {string|number|null|undefined} variantId
   * @returns {string|null}
   */
  normalizeVariantId(variantId) {
    if (variantId == null || variantId === '') return null;
    const raw = String(variantId);
    const parts = raw.split('/');
    return parts[parts.length - 1] || null;
  }

  async fetchCartData() {
    const now = Date.now();
    if (
      !this.isLoading &&
      !this.inflight &&
      this.lastFetchTime > 0 &&
      now - this.lastFetchTime < this.cacheTimeout
    ) {
      return this.cartItems;
    }

    if (this.inflight && this.inflightEpoch === this.cartEpoch) {
      return this.inflight;
    }

    const epoch = this.cartEpoch;
    this.isLoading = true;
    this.inflightEpoch = epoch;
    this.inflight = (async () => {
      try {
        const forceRefresh = this.lastFetchTime === 0;
        let cartData;
        if (typeof window.fetchSharedCart === 'function') {
          cartData = await window.fetchSharedCart({ force: forceRefresh });
        } else {
          const response = await fetch('/cart.js');
          cartData = await response.json();
        }
        if (epoch !== this.cartEpoch) {
          if (this.inflight && this.inflightEpoch === this.cartEpoch) {
            return this.inflight;
          }
          return this.fetchCartData();
        }
        this.cartItems = cartData.items || [];
        this.lastFetchTime = Date.now();
        return this.cartItems;
      } catch (error) {
        return this.cartItems;
      } finally {
        if (epoch === this.cartEpoch) {
          this.isLoading = false;
          this.inflight = null;
        }
      }
    })();

    return this.inflight;
  }

  async isVariantInCart(variantId) {
    const normalized = this.normalizeVariantId(variantId);
    if (!normalized) return false;
    const cartItems = await this.fetchCartData();
    return cartItems.some(
      item => this.normalizeVariantId(item.variant_id) === normalized
    );
  }

  async getCartItemByVariant(variantId) {
    const normalized = this.normalizeVariantId(variantId);
    if (!normalized) return null;
    const cartItems = await this.fetchCartData();
    return (
      cartItems.find(
        item => this.normalizeVariantId(item.variant_id) === normalized
      ) || null
    );
  }

  async getCartVariantIds() {
    const cartItems = await this.fetchCartData();
    return cartItems
      .map(item => this.normalizeVariantId(item.variant_id))
      .filter(Boolean);
  }

  async refreshCartData() {
    this.invalidateLocalCache();
    if (typeof window.invalidateSharedCart === 'function') {
      window.invalidateSharedCart();
    }
    return await this.fetchCartData();
  }

  async checkMultipleVariants(variantIds) {
    const cartVariantIds = await this.getCartVariantIds();
    const cartSet = new Set(cartVariantIds);
    const results = {};
    variantIds.forEach(variantId => {
      const key = String(variantId);
      const normalized = this.normalizeVariantId(variantId);
      results[key] = Boolean(normalized && cartSet.has(normalized));
    });
    return results;
  }

  async getVariantQuantityInCart(variantId) {
    const cartItem = await this.getCartItemByVariant(variantId);
    return cartItem && typeof cartItem === 'object' && 'quantity' in cartItem
      ? Number(cartItem.quantity)
      : 0;
  }

  clearCache() {
    this.cartItems = [];
    this.lastFetchTime = 0;
    this.cartEpoch += 1;
    this.inflight = null;
    this.isLoading = false;
  }
}

window.cartChecker = new CartChecker();
}
